'use client'

import cn from 'classnames'
import {useId, useRef, useState} from 'react'
import type {FinanceStackOption} from 'slices/RoiCalculator/components/FinanceStackSelect'
import {FinanceStackSelect} from 'slices/RoiCalculator/components/FinanceStackSelect'
import {MobileResultsDrawer} from 'slices/RoiCalculator/components/MobileResultsDrawer'
import {NumberField} from 'slices/RoiCalculator/components/NumberField'
import type {PresetOption} from 'slices/RoiCalculator/components/PresetPicker'
import {PresetPicker} from 'slices/RoiCalculator/components/PresetPicker'
import type {ResultsCardLayout} from 'slices/RoiCalculator/components/ResultsCard'
import {PeriodToggle, ResultsCard, useAnimatedNumber} from 'slices/RoiCalculator/components/ResultsCard'
import {Slider} from 'slices/RoiCalculator/components/Slider'
import {
  DEFAULT_HOURLY_RATE,
  DEFAULT_MINUTES_PER_TRANSACTION,
  DEFAULT_PRESET,
  DEFAULT_STACK,
  HOURS_PER_WORK_DAY,
  MAX_HOURLY_RATE,
  MAX_MINUTES_PER_TRANSACTION,
  MIN_HOURLY_RATE,
  MIN_MINUTES_PER_TRANSACTION,
  MONTHS_PER_YEAR,
  PRESET_KEYS,
  PRESETS,
  SLIDER_CONFIGS,
  VOLUME_KEYS,
} from 'slices/RoiCalculator/constants'
import {useResultsDrawerVisibility} from 'slices/RoiCalculator/hooks/useResultsDrawerVisibility'
import type {
  CurrencyCode,
  FinanceStack,
  PresetKey,
  PrimaryButton,
  RoiCalculatorLabels,
  SavingsInput,
  VolumeKey,
  Volumes,
} from 'slices/RoiCalculator/types'
import {
  calculateSavings,
  formatCompactCurrency,
  formatDays,
  formatNumber,
  getCurrencySymbol,
  MOBILE_COMPACT_THRESHOLD,
} from 'slices/RoiCalculator/utils'
import {ButtonV2} from 'src/common/ButtonV2'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'

type BackgroundColor = 'Grey' | 'White'

const BACKGROUND_COLOR_CLASS: Record<BackgroundColor, string> = {
  Grey: 'bg-moss-grey-100',
  White: 'bg-moss-white',
}

const TRACKING_LOCATION = 'roi-calculator'

export type RoiCalculatorProps = {
  locale: string
  currency: CurrencyCode
  eyebrow: string
  heading: string
  description: string
  disclaimer: string
  labels: RoiCalculatorLabels
  financeStackOptions: FinanceStackOption[]
  primaryButton: PrimaryButton | null
  pageUid: string
  backgroundColor: BackgroundColor | null
  paddingTop?: string | null
  paddingBottom?: string | null
  onShare: (report: never, captureElement: HTMLElement | null) => void
}

export const RoiCalculator = ({
  locale,
  currency,
  eyebrow,
  heading,
  description,
  disclaimer,
  labels,
  financeStackOptions,
  primaryButton,
  pageUid,
  backgroundColor,
  paddingTop,
  paddingBottom,
}: RoiCalculatorProps) => {
  const headingId = useId()
  const sectionRef = useRef<HTMLElement>(null)
  const [volumes, setVolumes] = useState<Volumes>(PRESETS[DEFAULT_PRESET])
  const [selectedPreset, setSelectedPreset] = useState<PresetKey | null>(DEFAULT_PRESET)
  const [minutesPerTransaction, setMinutesPerTransaction] = useState(DEFAULT_MINUTES_PER_TRANSACTION)
  const [hourlyRate, setHourlyRate] = useState(DEFAULT_HOURLY_RATE)
  const [stack, setStack] = useState<FinanceStack>(DEFAULT_STACK)
  const [hasInteracted, setHasInteracted] = useState(false)
  const [isYearly, setIsYearly] = useState(false)
  const controlsRef = useRef<HTMLDivElement>(null)
  const headlineRef = useRef<HTMLParagraphElement>(null)
  const isDrawerVisible = useResultsDrawerVisibility(controlsRef, headlineRef)

  const input: SavingsInput = {volumes, minutesPerTransaction, hourlyRate, stack}
  const savings = calculateSavings(input)
  const periodMultiplier = isYearly ? MONTHS_PER_YEAR : 1
  const drawerDays = useAnimatedNumber(Math.round(((savings.totalHours * periodMultiplier) / HOURS_PER_WORK_DAY) * 10))
  const drawerMoney = useAnimatedNumber(savings.money * periodMultiplier)
  const presetOptions: PresetOption[] = PRESET_KEYS.map((key) => ({key, label: labels.presets[key]}))
  const paddingTopClass = PADDING_TOP_CLASS[paddingTop ?? DEFAULT_PADDING] ?? ''
  const paddingBottomClass = PADDING_BOTTOM_CLASS[paddingBottom ?? DEFAULT_PADDING] ?? ''

  const markInteracted = () => {
    if (!hasInteracted) {
      setHasInteracted(true)
    }
  }

  const selectPreset = (presetKey: PresetKey) => {
    setVolumes(PRESETS[presetKey])
    setSelectedPreset(presetKey)
    markInteracted()
  }

  const changeVolume = (volumeKey: VolumeKey, value: number) => {
    if (value === volumes[volumeKey]) {
      return
    }

    setVolumes({...volumes, [volumeKey]: value})
    setSelectedPreset(null)
    markInteracted()
  }

  const changeHourlyRate = (value: number) => {
    setHourlyRate(value)
    markInteracted()
  }

  const changeMinutesPerTransaction = (value: number) => {
    setMinutesPerTransaction(value)
    markInteracted()
  }

  const changePeriod = (yearly: boolean) => {
    setIsYearly(yearly)
    markInteracted()
  }

  const changeStack = (value: FinanceStack) => {
    setStack(value)
    markInteracted()
  }

  const renderPrimaryButton = () =>
    primaryButton && (
      <ButtonV2
        href={hasInteracted ? primaryButton.href : undefined}
        variant={hasInteracted ? 'primary' : 'secondary'}
        className={cn('w-full', !hasInteracted && 'cursor-not-allowed opacity-40')}
        disabled={!hasInteracted}
        trackingData={{
          location: TRACKING_LOCATION,
          dataType: 'primary-button',
          mktgId: primaryButton.marketingId,
          pageName: pageUid,
        }}
      >
        {primaryButton.label}
      </ButtonV2>
    )

  const renderResultsCard = (layout: ResultsCardLayout) => (
    <ResultsCard
      savings={savings}
      locale={locale}
      currency={currency}
      hoursLabel={labels.hoursSaved}
      formatHours={labels.formatHours}
      moneyLabel={labels.moneySaved}
      breakdownLabels={labels.breakdown}
      resultsLabels={labels.results}
      hourlyRate={hourlyRate}
      isYearly={isYearly}
      onPeriodChange={changePeriod}
      layout={layout}
      headlineRef={layout === 'card' ? headlineRef : undefined}
    >
      {renderPrimaryButton()}
    </ResultsCard>
  )

  return (
    <section
      ref={sectionRef}
      data-slice-type="roi_calculator"
      data-slice-variation="default"
      aria-labelledby={heading ? headingId : undefined}
      className={cn(
        BACKGROUND_COLOR_CLASS[backgroundColor ?? 'Grey'],
        'px-5 md:px-10',
        paddingTopClass,
        paddingBottomClass,
      )}
    >
      <div className="mx-auto flex max-w-container flex-col gap-10">
        <header className="flex max-w-[550px] flex-col gap-6">
          {eyebrow && (
            <p className="text-12 font-semibold uppercase leading-[normal] tracking-[0.72px] text-blue-550">
              {eyebrow}
            </p>
          )}
          {heading && (
            <h1
              id={headingId}
              className="max-w-[510px] text-40 font-medium leading-tight text-moss-black md:text-48 md:leading-[56px]"
            >
              {heading}
            </h1>
          )}
          {description && <p className="max-w-[360px] text-18 leading-6 text-moss-grey-550">{description}</p>}
        </header>

        <div className="grid gap-10 lg:grid-cols-[minmax(0,510px)_minmax(0,587px)] lg:justify-between lg:gap-x-12">
          {/* Left column — controls */}
          <div ref={controlsRef} className="flex flex-col gap-12">
            <PresetPicker
              label={labels.presetsLabel}
              options={presetOptions}
              selected={selectedPreset}
              onSelect={selectPreset}
            />

            {/* Sliders grouped tightly */}
            <div className="flex flex-col gap-5">
              {VOLUME_KEYS.map((volumeKey) => (
                <Slider
                  key={volumeKey}
                  label={labels.volumes[volumeKey]}
                  value={volumes[volumeKey]}
                  config={SLIDER_CONFIGS[volumeKey]}
                  formatValue={(value) => formatNumber(value, locale)}
                  unitSuffix={labels.results.perMonthShort}
                  showPulse={!hasInteracted}
                  onChange={(value) => changeVolume(volumeKey, value)}
                />
              ))}
            </div>

            {/* Number input fields */}
            <div className="grid items-start gap-4 sm:grid-cols-2">
              <NumberField
                label={labels.hourlyRate}
                value={hourlyRate}
                min={MIN_HOURLY_RATE}
                max={MAX_HOURLY_RATE}
                step={5}
                prefix={getCurrencySymbol(locale, currency)}
                hint={labels.hints.hourlyRate}
                onChange={changeHourlyRate}
              />
              <NumberField
                label={labels.minutesPerTransaction}
                value={minutesPerTransaction}
                min={MIN_MINUTES_PER_TRANSACTION}
                max={MAX_MINUTES_PER_TRANSACTION}
                hint={labels.hints.minutesPerTransaction}
                onChange={changeMinutesPerTransaction}
              />
            </div>

            <FinanceStackSelect
              label={labels.financeStack}
              options={financeStackOptions}
              value={stack}
              onChange={changeStack}
            />
          </div>

          {/* Right column — results */}
          <div className="flex flex-col gap-5">
            <div className="lg:sticky lg:top-24">
              {renderResultsCard('card')}
              {disclaimer && <p className="mt-3 text-12 leading-tight text-black-60">{disclaimer}</p>}
            </div>
          </div>
        </div>
      </div>
      <MobileResultsDrawer
        isVisible={hasInteracted && isDrawerVisible}
        timeLabel={labels.hoursSaved}
        timeValue={`${formatDays(drawerDays / 10)} ${labels.results.days}`}
        moneyLabel={labels.moneySaved}
        moneyValue={formatCompactCurrency(drawerMoney, locale, currency, MOBILE_COMPACT_THRESHOLD)}
        periodSuffix={isYearly ? labels.results.perYearShort : labels.results.perMonthShort}
        expandLabel={labels.results.showFullResults}
        collapseLabel={labels.results.hideFullResults}
        headerAction={
          <PeriodToggle
            isYearly={isYearly}
            monthlyLabel={labels.results.monthly}
            yearlyLabel={labels.results.yearly}
            onChange={changePeriod}
          />
        }
      >
        {renderResultsCard('drawer')}
      </MobileResultsDrawer>
    </section>
  )
}
